import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, NavLink as RouterNavLink, useSearchParams } from 'react-router-dom';
import { Image } from 'semantic-ui-react';
import { Menu } from '../../layout/Menu';
import { device } from '../../../theme';

// Import club statistics data
let clubStatisticsData = {};
try {
  clubStatisticsData = require('../../../data/club-statistics.json');
} catch (e) {
  console.warn('Club statistics data not yet generated. Run: npm run generate-person-statistics');
}

export const ClubStatistics = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCompetition, setSelectedCompetition] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('goldCount');
  const [sortDirection, setSortDirection] = useState('desc');

  const competitions = useMemo(() => {
    return Object.keys(clubStatisticsData)
      .filter(key => Object.keys(clubStatisticsData[key]).length > 0)
      .sort((a, b) => a.localeCompare(b, 'nb')); // Sort alphabetically with Norwegian locale
  }, []);

  const years = useMemo(() => {
    if (!selectedCompetition || !clubStatisticsData[selectedCompetition]) {
      return [];
    }
    return Object.keys(clubStatisticsData[selectedCompetition]).sort((a, b) => {
      if (a === 'all') return 1;
      if (b === 'all') return -1;

      // Extract year numbers for sorting (handles both "2014" and "2014 Høst" formats)
      const yearA = parseInt(a);
      const yearB = parseInt(b);

      // If years are different, sort by year (newest first)
      if (yearA !== yearB) {
        return yearB - yearA;
      }

      // If same year, sort by part (Vår before Høst)
      return a.localeCompare(b, 'nb');
    });
  }, [selectedCompetition]);

  const clubData = useMemo(() => {
    if (!selectedCompetition || !selectedYear || !clubStatisticsData[selectedCompetition]?.[selectedYear]) {
      return [];
    }
    return clubStatisticsData[selectedCompetition][selectedYear];
  }, [selectedCompetition, selectedYear]);

  const sortedData = useMemo(() => {
    const sorted = [...clubData];
    sorted.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      // Handle undefined/null values
      if (aVal === undefined || aVal === null) aVal = 0;
      if (bVal === undefined || bVal === null) bVal = 0;

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
        if (sortDirection === 'asc') {
          return aVal.localeCompare(bVal);
        } else {
          return bVal.localeCompare(aVal);
        }
      }

      // Numeric comparison
      if (sortDirection === 'asc') {
        return aVal - bVal;
      } else {
        return bVal - aVal;
      }
    });
    return sorted;
  }, [clubData, sortColumn, sortDirection]);

  const filteredDataWithRanks = useMemo(() => {
    const term = searchTerm.toLowerCase();

    // Assign ranks based on sort column value (handle ties)
    const dataWithRanks = [];
    let currentRank = 1;
    let previousValue = null;

    sortedData.forEach((club, index) => {
      const currentValue = club[sortColumn];

      // If value changed, update rank
      if (index > 0 && currentValue !== previousValue) {
        currentRank = index + 1;
      }

      dataWithRanks.push({
        ...club,
        rank: currentRank
      });

      previousValue = currentValue;
    });

    // Filter after ranking
    return dataWithRanks.filter(club =>
      !searchTerm ||
      club.clubName.toLowerCase().includes(term)
    );
  }, [sortedData, searchTerm, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Initialize from URL or set default
  useEffect(() => {
    const urlComp = searchParams.get('competition');
    if (urlComp && competitions.includes(urlComp)) {
      setSelectedCompetition(urlComp);
    } else if (!selectedCompetition && competitions.length > 0) {
      // Prefer 'NM' if available, otherwise use first competition
      const defaultComp = competitions.includes('NM') ? 'NM' : competitions[0];
      setSelectedCompetition(defaultComp);
      setSearchParams({ competition: defaultComp });
    }
  }, [competitions]);

  // Update URL when competition changes
  const handleCompetitionChange = (comp) => {
    setSelectedCompetition(comp);
    setSearchParams({ competition: comp });
  };

  React.useEffect(() => {
    if (years.length > 0 && !years.includes(selectedYear)) {
      setSelectedYear('all');
    }
  }, [years, selectedYear]);

  return (
    <>
      <Header>
        <div>
          <a href="https://nsff.no">
            <Image src={"/resultater/images/logo-darkgreen.png"} alt="Logo" height={"60"} />
          </a>
        </div>
        <div>
          <h1>Klubbstatistikk</h1>
        </div>
        <div>
          <Link to={"/"}>Tilbake</Link>
        </div>
      </Header>
      <Menu />
      <Container>
        <ContentWrapper>
          <Title>Klubbstatistikk</Title>

          <NavLinks>
            <NavLink to={`/statistikk${selectedCompetition ? `?competition=${selectedCompetition}` : ''}`} end>Konkurranser</NavLink>
            <NavLink to={`/statistikk/personer${selectedCompetition ? `?competition=${selectedCompetition}` : ''}`}>Personer</NavLink>
            <NavLink to={`/statistikk/klubber${selectedCompetition ? `?competition=${selectedCompetition}` : ''}`}>Klubber</NavLink>
            <NavLink to="/statistikk/hederstegn">Hederstegn</NavLink>
            <NavLink to="/statistikk/nsff">NSFF</NavLink>
          </NavLinks>

          <FilterRow>
            <FilterGroup>
              <label htmlFor="competition-select">Konkurranse: </label>
              <select
                id="competition-select"
                value={selectedCompetition}
                onChange={(e) => handleCompetitionChange(e.target.value)}
              >
                {competitions.map(comp => (
                  <option key={comp} value={comp}>{comp}</option>
                ))}
              </select>
            </FilterGroup>

            <FilterGroup>
              <label htmlFor="year-select">År: </label>
              <select
                id="year-select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {years.map(year => (
                  <option key={year} value={year}>
                    {year === 'all' ? 'Alle år' : year}
                  </option>
                ))}
              </select>
            </FilterGroup>

            <FilterGroup>
              <label htmlFor="search">Søk: </label>
              <SearchInput
                id="search"
                type="text"
                placeholder="Søk etter klubb..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </FilterGroup>
          </FilterRow>

          {filteredDataWithRanks.length > 0 && (
            <>
              <ResultCount>Viser {filteredDataWithRanks.length} {searchTerm && `av ${sortedData.length}`} klubber</ResultCount>
              <TableWrapper>
                <StatsTable>
                  <thead>
                    <tr>
                      <th>#</th>
                      <SortableHeader onClick={() => handleSort('clubName')}>
                        Klubb {sortColumn === 'clubName' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('totalParticipants')}>
                        Deltakere {sortColumn === 'totalParticipants' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('totalAwards')}>
                        Sum {sortColumn === 'totalAwards' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('goldCount')}>
                        Gull {sortColumn === 'goldCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('silverCount')}>
                        Sølv {sortColumn === 'silverCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('bronzeCount')}>
                        Bronse {sortColumn === 'bronzeCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('hmCount')}>
                        HM {sortColumn === 'hmCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('acceptedCount')}>
                        Antatt {sortColumn === 'acceptedCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDataWithRanks.map((club, idx) => (
                      <tr key={`${club.clubName}-${idx}`}>
                        <RankCell>{club.rank}</RankCell>
                        <td>{club.clubName}</td>
                        <td>{club.totalParticipants}</td>
                        <td>{club.totalAwards}</td>
                        <td>{club.goldCount || '-'}</td>
                        <td>{club.silverCount || '-'}</td>
                        <td>{club.bronzeCount || '-'}</td>
                        <td>{club.hmCount || '-'}</td>
                        <td>{club.acceptedCount || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </StatsTable>
              </TableWrapper>
            </>
          )}

          {filteredDataWithRanks.length === 0 && clubData.length === 0 && (
            <NoData>Ingen statistikk tilgjengelig for dette valget</NoData>
          )}

          {filteredDataWithRanks.length === 0 && clubData.length > 0 && (
            <NoData>Ingen treff for søket "{searchTerm}"</NoData>
          )}
        </ContentWrapper>
      </Container>
    </>
  );
};

const Container = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 20px;
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  height: 70px;
  background-color: #90b361;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: #36501c;
  font-family: 'Fibon Neue';

  > div {
    display: flex;
    flex-direction: row;
    margin-left: 10px;

    > div {
      margin-left: 20px;
    }

    > a, > h1 {
      color: ${(p) => p.theme.color.primary.base};
    }

    align-items: center;
  }

  > div:nth-child(2) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    font-size: 1.4rem;

    h1 {
      margin: 0;
      font-size: 1.4rem;
      font-weight: normal;
    }
  }

  > div:nth-child(3) {
    margin-right: 20px;
    > a {
      color: ${(p) => p.theme.color.primary.base};
    }
  }
`;

const Title = styled.h1`
  color: #36501c;
  font-family: 'Fibon Neue';
  margin-bottom: 30px;
  text-align: center;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;
  justify-content: center;

  @media ${device.mobile} {
    flex-direction: column;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }

  label {
    font-weight: bold;
    color: #36501c;
    white-space: nowrap;

    @media ${device.mobile} {
      align-self: flex-start;
    }
  }

  select {
    padding: 8px 15px;
    border: 2px solid #90b361;
    border-radius: 4px;
    font-size: 16px;
    background-color: white;
    color: #36501c;
    cursor: pointer;
    min-width: 150px;

    &:focus {
      outline: none;
      border-color: #36501c;
    }

    @media ${device.mobile} {
      min-width: 100%;
    }
  }
`;

const SearchInput = styled.input`
  padding: 8px 15px;
  border: 2px solid #90b361;
  border-radius: 4px;
  font-size: 16px;
  min-width: 250px;

  &:focus {
    outline: none;
    border-color: #36501c;
  }

  @media ${device.mobile} {
    min-width: 100%;
  }
`;

const ResultCount = styled.div`
  text-align: center;
  margin-bottom: 15px;
  color: #666;
  font-style: italic;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media ${device.mobile} {
    margin-left: -30px;
    margin-right: -30px;
    padding: 0 15px;
  }
`;

const StatsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;

  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
    white-space: nowrap;
  }

  th {
    background-color: #90b361;
    color: #36501c;
    font-weight: bold;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  tbody tr:hover {
    background-color: #f9f9f9;
  }

  td:nth-child(1), td:nth-child(3), td:nth-child(4), td:nth-child(5),
  td:nth-child(6), td:nth-child(7), td:nth-child(8), td:nth-child(9),
  th:nth-child(1), th:nth-child(3), th:nth-child(4), th:nth-child(5),
  th:nth-child(6), th:nth-child(7), th:nth-child(8), th:nth-child(9) {
    text-align: center;
  }

  @media ${device.mobile} {
    font-size: 11px;
    min-width: 700px;

    th, td {
      padding: 6px 3px;
    }
  }
`;

const SortableHeader = styled.th`
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: #7a9851;
  }
`;

const NoData = styled.div`
  text-align: center;
  padding: 50px;
  color: #666;
  font-size: 1.2rem;
`;

const NavLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;
`;

const NavLink = styled(RouterNavLink)`
  padding: 10px 20px;
  border: 2px solid #90b361;
  border-radius: 4px;
  text-decoration: none;
  color: #36501c;
  font-weight: bold;
  transition: all 0.2s;

  &:hover {
    background-color: #90b361;
    color: white;
  }

  &.active {
    background-color: #36501c;
    border-color: #36501c;
    color: white;
  }
`;

const RankCell = styled.td`
  font-weight: bold;
  color: #666;
`;
